import { useEffect, useRef } from "react";

import { isEmptyAccessioningNotesHtml } from "@blis/shared/schemas/accessioning-notes";

import { Description, FieldError, Label } from "@/components/ui/field";
import { cn } from "@/components/ui/utils";
import { sanitizeHtmlToFragment } from "@/features/admin/utils/html";

type HtmlEditorProps = {
  id?: string;
  label: string;
  description?: string;
  error?: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  className?: string;
};

export function HtmlEditor({
  id,
  label,
  description,
  error,
  value,
  onChange,
  className,
}: HtmlEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const squireRef = useRef<import("squire-rte").default | null>(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!editorRef.current || squireRef.current) return;

    let cancelled = false;

    import("squire-rte").then(({ default: Squire }) => {
      if (cancelled || !editorRef.current) return;

      const editor = new Squire(editorRef.current, {
        sanitizeToDOMFragment: sanitizeHtmlToFragment,
      });
      squireRef.current = editor;

      const initial = initialValueRef.current;
      if (initial) {
        editor.setHTML(initial);
      }

      editor.addEventListener("input", () => {
        const html = editor.getHTML();
        onChangeRef.current(isEmptyAccessioningNotesHtml(html) ? null : html);
      });
    });

    return () => {
      cancelled = true;
      squireRef.current?.destroy();
      squireRef.current = null;
    };
  }, []);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Label htmlFor={id}>{label}</Label>
      <div
        id={id}
        ref={editorRef}
        className="outline-hidden px-3 py-2 border rounded-lg min-h-[80px] max-h-52 overflow-auto text-sm focus-within:border-ring focus-within:outline-hidden focus-within:ring-[3px] focus-within:ring-ring/20 transition-shadow"
        onKeyDown={(event) => {
          // Keep Cmd/Ctrl formatting shortcuts in the editor (e.g. Cmd-B bold)
          // instead of letting them hit app-level window listeners.
          if (event.metaKey || event.ctrlKey) {
            event.stopPropagation();
          }
        }}
      />
      {description ? <Description>{description}</Description> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}
